import * as React from 'react'
import { Helmet } from 'react-helmet'
import { Trans, withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { AnyAction, Dispatch } from 'redux'
import { Building2Icon, CheckIcon, LogOutIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react'
import { AppState } from '../store'
import { UserController } from '../controllers'
import Utils from '../utilities/Utils'
import { showConfirmModal } from '../store/modals/actions'
import Notification from '../utilities/Notification'
import UrlHelper from '../helpers/UrlHelper'
import ERoute from '../ERoute'
import { CurrentUser, UserWorkspaceSetting, Workspace } from '../types'
import Images from '../images'
import { cn } from '../lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Button } from '../components/ui/button'
import { Spinner } from '../components/ui/spinner'

interface IStateToProps {
	currentUser: CurrentUser
}

interface IDispatchToProps {
	showConfirmModal: typeof showConfirmModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation & RouteComponentProps<{ inviteToken?: string }>

interface IState {
	didInitialLoad: boolean
	invitedWorkspace?: UserWorkspaceSetting
	userWorkspaceSettings: UserWorkspaceSetting[]
	currentPage: number,
	reachedEnd: boolean
	isFetching: boolean
}

class Workspaces extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		this.state = {
			invitedWorkspace: null,
			userWorkspaceSettings: [],
			currentPage: 0,
			reachedEnd: false,
			didInitialLoad: false,
			isFetching: false,
		}

		this.onWorkspaceClick = this.onWorkspaceClick.bind(this)
		this.onNewWorkspaceClick = this.onNewWorkspaceClick.bind(this)
		this.onLoadMoreClick = this.onLoadMoreClick.bind(this)
		this.onBackClick = this.onBackClick.bind(this)
		this.onAcceptInviteClick = this.onAcceptInviteClick.bind(this)
		this.onDeclineInviteClick = this.onDeclineInviteClick.bind(this)
		this.onLeaveWorkspaceClick = this.onLeaveWorkspaceClick.bind(this)
		this.onDeleteWorkspaceClick = this.onDeleteWorkspaceClick.bind(this)
	}

	componentDidMount() {
		this.fetchWorkspaces(1)
		this.parseQueryParams()
	}

	parseQueryParams() {
		const queryParams = UrlHelper.getParams(this.props.location.search)

		if (queryParams.inviteToken) {
			this.fetchInvitedWorkspace(queryParams.inviteToken).catch(console.error)
		}
	}

	async fetchInvitedWorkspace(inviteToken: string) {
		try {
			const invitedWorkspace = await UserController.getInviteWorkspace(inviteToken)

			this.setState({ invitedWorkspace: invitedWorkspace })
		} catch (ex) {
			console.error(ex)
		}
	}

	async fetchWorkspaces(page: number) {
		const { match: { params: { inviteToken } } } = this.props

		try {
			const response = await UserController.getWorkspaces({ page: 1, order: 'created_at_asc', invite_token: inviteToken })
			const { userWorkspaceSettings: stateUserWorkspaceSettings } = this.state;
			const { user_workspace_settings, current_page, total_pages, total_entries } = response

			this.setState({
				userWorkspaceSettings: page === 1 ? [...user_workspace_settings] : [...stateUserWorkspaceSettings, ...user_workspace_settings],
				currentPage: current_page,
				reachedEnd: current_page === total_pages,
				didInitialLoad: true,
				isFetching: false
			});

		} catch (ex) {
			console.error(ex)
		}
	}

	async onWorkspaceClick(workspaceId: string) {
		try {
			const response = await UserController.updateUser({ workspace_id: workspaceId })

			// If update succesful reload current page to load the new workspace data
			if (response) window.location.replace('/')
		} catch (ex) {
			console.error(ex)
		}
	}

	onNewWorkspaceClick() {
		const { t } = this.props

		requestAnimationFrame(() => {
			this.props.showConfirmModal({
				title: t('Workspaces::New workspace'),
				description: t('Workspaces::You are about to create a new workspace.'),
				action: { label: t('Workspaces::Create my workspace') },
				onConfirm: async () => {
					try {
						const response = await UserController.createWorkspace()

						if (response.errors) {
							Notification.notifyError(t('Workspaces::Oops something went wrong'));
						} else if (response) {
							const workspace: Workspace = response
							this.onWorkspaceClick(workspace.id)
						}
					} catch (ex) {
						console.error(ex)
					}
				}
			})
		})
	}

	onLoadMoreClick() {
		const { currentPage } = this.state;

		this.fetchWorkspaces(currentPage + 1);
	}

	onBackClick() {
		this.props.history.goBack()
	}

	async onAcceptInviteClick() {
		const { invitedWorkspace } = this.state
		const queryParams = UrlHelper.getParams(this.props.location.search)

		if (invitedWorkspace && queryParams.inviteToken) {
			try {
				const response = await UserController.acceptWorkspaceInvite(queryParams.inviteToken)

				if (response.success) {
					this.onWorkspaceClick(invitedWorkspace.workspace_id)
				}
			} catch (ex) {
				console.error(ex)
			}
		}
	}

	onDeclineInviteClick() {
		this.setState({ invitedWorkspace: null })
		this.props.history.replace(ERoute.PATH_WORKSPACES)
	}

	async onLeaveWorkspaceClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, userWorkspaceSetting: UserWorkspaceSetting) {
		const { t } = this.props
		e.preventDefault()
		e.stopPropagation()

		const { userWorkspaceSettings } = this.state

		requestAnimationFrame(() => {
			this.props.showConfirmModal({
				title: t('Workspaces::Leaving workspace'),
				description: t('Workspaces::You are about to leave the selected workspace.'),
				action: { label: t('Workspaces::Leave'), isDestructive: true },
				onConfirm: async () => {
					try {
						const response = await UserController.leaveWorkspace(userWorkspaceSetting.id)

						if (response.success) {
							const userWorkspaceSettingIndex = userWorkspaceSettings.findIndex(setting => setting.id === userWorkspaceSetting.id)

							userWorkspaceSettings.splice(userWorkspaceSettingIndex, 1);

							this.setState({ userWorkspaceSettings: [...userWorkspaceSettings] });
						}
					} catch (ex) {
						console.error(ex)
					}
				}
			})
		})
	}

	async onDeleteWorkspaceClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, userWorkspaceSetting: UserWorkspaceSetting) {
		e.preventDefault()
		e.stopPropagation()

		const { userWorkspaceSettings } = this.state
		const { t } = this.props

		requestAnimationFrame(() => {
			this.props.showConfirmModal({
				title: t('Workspaces::Deleting workspace'),
				description: t('Workspaces::You are about to delete the selected workspace. This action is irreversible and all data will be removed. Are you sure?'),
				action: { label: t('Workspaces::Delete workspace'), isDestructive: true },
				onConfirm: async () => {
					try {
						UserController.deleteWorkspace(userWorkspaceSetting.workspace_id).catch(console.error)

						const userWorkspaceSettingIndex = userWorkspaceSettings.findIndex(setting => setting.id === userWorkspaceSetting.id)

						userWorkspaceSettings.splice(userWorkspaceSettingIndex, 1);

						this.setState({ userWorkspaceSettings: [...userWorkspaceSettings] });
					} catch (ex) {
						console.error(ex)
					}
				}
			})
		})
	}

	renderWorkspaceRow(userWorkspaceSetting: UserWorkspaceSetting, actions: React.ReactNode, onClick?: () => void) {
		const { t } = this.props
		const { workspace } = userWorkspaceSetting
		const businessName = workspace.business_name
		const businessLogo = workspace.business_logo_url

		return (
			<div
				key={workspace.id}
				onClick={onClick}
				className={cn(
					'flex items-center gap-3 px-5 py-3.5',
					onClick && 'cursor-pointer hover:bg-muted/50'
				)}
			>
				<Avatar size="lg" className="rounded-lg">
					{businessLogo && <AvatarImage src={businessLogo} />}
					<AvatarFallback className="rounded-lg bg-muted-foreground text-white font-semibold">
						{Utils.getInitials(businessName || 'Untitled')}
					</AvatarFallback>
				</Avatar>

				<div className="min-w-0 flex-1">
					<div className="truncate text-sm font-semibold text-foreground">
						{businessName !== '' ? businessName : 'Untitled'}
					</div>
					<div className="text-xs text-muted-foreground">
						{t('Workspaces::{{count}} member', { count: workspace.users_count })}
					</div>
				</div>

				<div className="flex items-center gap-1.5">
					{actions}
				</div>
			</div>
		)
	}

	render() {
		const { currentUser, t } = this.props
		const { invitedWorkspace, userWorkspaceSettings, reachedEnd, didInitialLoad } = this.state

		return (
			<div className="flex min-h-full w-full flex-col items-center overflow-x-hidden overflow-y-auto bg-background px-4 pt-16 pb-10 font-sans! md:pt-20">
				<Helmet>
					<title>{t('Workspaces::{{__appName}} | Workspaces')}</title>
				</Helmet>

				<a href="/">
					<img src={Images.LOGO_TEXT} className="h-8 w-auto" />
				</a>

				<h1 className="mt-108 text-3xl font-bold! tracking-tight text-foreground">
					{t('Workspaces::Welcome back!')}
				</h1>
				<p className="mt-1! text-sm text-muted-foreground">
					{t("Workspaces::Choose the workspace you'd like to switch to!")}
				</p>

				{!didInitialLoad && (
					<div className="mt-12 flex items-center justify-center">
						<Spinner className="size-6 text-muted-foreground" />
					</div>
				)}

				{didInitialLoad && <>
					{invitedWorkspace && (
						<div className="mt-8 w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card">
							<div className="border-b border-border px-5 py-3.5">
								<Trans t={t}>
									<p className="text-sm text-foreground">
										Pending workspace invite
									</p>
								</Trans>
							</div>

							<div className="divide-y divide-border">
								{this.renderWorkspaceRow(invitedWorkspace, <>
									<Button variant="outline" size="icon" onClick={() => this.onAcceptInviteClick()}>
										<CheckIcon />
									</Button>
									<Button variant="destructive" size="icon" onClick={() => this.onDeclineInviteClick()}>
										<XIcon />
									</Button>
								</>)}
							</div>
						</div>
					)}

					<div className="mt-8 w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card">
						<div className="border-b border-border px-5 py-3.5">
							<Trans t={t}>
								<p className="text-sm text-foreground">
									Workspaces for <b className="font-semibold">{currentUser.email}</b>
								</p>
							</Trans>
						</div>

						<div className="divide-y divide-border">
							{userWorkspaceSettings.length === 0 && (
								<div className="flex flex-col items-center justify-center gap-2 px-5 py-10 text-center">
									<Building2Icon className="size-6 text-muted-foreground" />
									<p className="text-sm text-muted-foreground">
										{t('Workspaces::No workspaces available')}
									</p>
									<Button variant="outline" size="sm" onClick={this.onNewWorkspaceClick}>
										{t('Workspaces::Create workspace')}
									</Button>
								</div>
							)}

							{userWorkspaceSettings.map(userWorkspaceSetting => this.renderWorkspaceRow(
								userWorkspaceSetting,
								!userWorkspaceSetting.admin ? (
									<Button variant="ghost" size="icon" onClick={(e) => this.onLeaveWorkspaceClick(e, userWorkspaceSetting)}>
										<LogOutIcon />
									</Button>
								) : (
									<Button variant="destructive" size="icon" onClick={(e) => this.onDeleteWorkspaceClick(e, userWorkspaceSetting)}>
										<Trash2Icon />
									</Button>
								),
								() => this.onWorkspaceClick(userWorkspaceSetting.workspace.id)
							))}
						</div>
					</div>
				</>}

				{didInitialLoad && !reachedEnd && (
					<div className="mt-4">
						<Button variant="outline" onClick={this.onLoadMoreClick}>
							{t('Workspaces::Load more')}
						</Button>
					</div>
				)}

				<div className="mt-8 flex w-full max-w-lg flex-col items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-5 py-4 sm:flex-row">
					<p className="text-sm text-foreground">
						{t('Workspaces::Want to use {{__appName}} with a different team?')}
					</p>

					<Button variant="outline" onClick={this.onNewWorkspaceClick}>
						<PlusIcon />
						{t('Workspaces::Create workspace')}
					</Button>
				</div>

				<Button variant="link" className="mt-6!" onClick={this.onBackClick}>
					{t('Workspaces::Back')}
				</Button>
			</div>
		)
	}
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
	return {
		showConfirmModal: (options) => dispatch(showConfirmModal(options))
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Workspaces))
